import { useCallback, useEffect, useRef, useState } from 'react';
import type { Guard } from '@movienight/shared';
import { warmArmSprites } from './preload';

/**
 * The bribe. There is no checkbox; you bribe a guard. (DESIGN.md §5)
 *
 * **The arm is assembled at runtime, not a single image.** Every piece is 71px
 * wide: `arm_base` is the hand, `arm_fingers` is a foreground layer, and
 * `arm_extender` (71×66) tiles vertically. The note sits *between* base and
 * fingers so it reads as gripped rather than pasted on.
 *
 * The arm's length is not fixed — it is computed from where the hand is. At
 * rest only the hand shows, poking above the bottom edge. Grab it and it
 * follows your pointer while the forearm stretches from the bottom of the
 * screen up to wherever the hand now is. That is why the extender exists as a
 * separate tile: the arm has to be able to reach any point on any viewport.
 *
 * Both input paths work, and both are required:
 *   drag     press the hand, move, release over a bouncer
 *   tap-tap  tap the hand (it follows), then tap a bouncer
 * Long-press-then-drag fights scrolling on touch, and this is a first-run
 * interaction where confusion costs you a user.
 */

const SCALE = 2;
const PIECE_W = 71 * SCALE;
const HAND_H = 178 * SCALE;
const EXT_H = 66 * SCALE;

/** How much of the hand peeks above the bottom edge while it waits. */
const REST_PEEK = 132;

/** Movement beyond this counts as a drag rather than a tap. */
const DRAG_THRESHOLD = 10;

type Phase = 'resting' | 'held' | 'given';

export function Bribe({
  alreadyBribed,
  onChoose,
}: {
  alreadyBribed: Guard | null;
  onChoose: (guard: Guard | null) => void;
}) {
  const [phase, setPhase] = useState<Phase>('resting');
  const [hovered, setHovered] = useState<Guard | null>(null);
  const [risen, setRisen] = useState(false);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);

  const pressOrigin = useRef<{ x: number; y: number } | null>(null);
  const moved = useRef(false);

  // The resting position and the arm length are both measured against the
  // viewport, so a rotate or a resize has to re-render or the arm detaches
  // from the bottom edge.
  const [viewport, setViewport] = useState(() => ({
    w: typeof window === 'undefined' ? 390 : window.innerWidth,
    h: typeof window === 'undefined' ? 844 : window.innerHeight,
  }));
  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  const reduceMotion =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    // Backstop. The door normally warms these the moment it becomes usable,
    // but the bribe is also reachable on paths that never rendered the door
    // scene. Idempotent, so calling it twice costs nothing.
    warmArmSprites();

    // prefers-reduced-motion: the hand appears without the rise animation.
    if (reduceMotion) {
      setRisen(true);
      return;
    }
    const id = window.setTimeout(() => setRisen(true), 80);
    return () => clearTimeout(id);
  }, [reduceMotion]);

  const guardAt = useCallback((x: number, y: number): Guard | null => {
    // elementsFromPoint rather than cached rects: the portraits can reflow on
    // rotate, and a stale rectangle means the note lands on nothing.
    for (const el of document.elementsFromPoint(x, y)) {
      const guard = el.closest?.('[data-guard]')?.getAttribute('data-guard');
      if (guard === 'armane' || guard === 'versace') return guard;
    }
    return null;
  }, []);

  const commit = useCallback(
    (guard: Guard | null) => {
      setPhase('given');
      setPos(null);
      onChoose(guard);
    },
    [onChoose],
  );

  // While held, the hand tracks the pointer anywhere on screen — including
  // after the finger lifts, which is what makes the tap-tap path work.
  useEffect(() => {
    if (phase !== 'held') return;

    const onMove = (e: PointerEvent) => {
      if (pressOrigin.current) {
        const dx = e.clientX - pressOrigin.current.x;
        const dy = e.clientY - pressOrigin.current.y;
        if (Math.hypot(dx, dy) > DRAG_THRESHOLD) moved.current = true;
      }
      setPos({ x: e.clientX, y: e.clientY });
      setHovered(guardAt(e.clientX, e.clientY));
    };

    const onUp = (e: PointerEvent) => {
      const guard = guardAt(e.clientX, e.clientY);
      if (guard) {
        commit(guard);
        return;
      }
      // A drag that ended on nothing puts the hand back. A tap leaves it
      // following, waiting for a second tap on a bouncer.
      if (moved.current) {
        setPhase('resting');
        setPos(null);
        setHovered(null);
      }
      pressOrigin.current = null;
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [phase, guardAt, commit]);

  if (phase === 'given') return null;

  const following = phase === 'held' && pos !== null;
  // Top edge of the hand sprite in viewport coordinates.
  const handTop = following ? pos.y - HAND_H * 0.18 : viewport.h - REST_PEEK;
  const handLeft = following ? pos.x - PIECE_W / 2 : viewport.w / 2 - PIECE_W / 2;
  // The forearm fills whatever is left between the hand and the bottom edge.
  // At rest the hand already runs off the bottom, so this is zero and no
  // forearm is drawn at all — which is the point: only the hand shows.
  const armTop = handTop + HAND_H - 4;
  const armHeight = Math.max(0, viewport.h - armTop);

  return (
    <>
      {/* Forearm: only exists while the hand is away from its resting place. */}
      {armHeight > 0 && (
        <div
          aria-hidden
          className="sprite pointer-events-none fixed z-40"
          style={{
            left: handLeft,
            top: armTop,
            width: PIECE_W,
            height: armHeight,
            backgroundImage: 'url(/assets/arm_extender.png)',
            backgroundRepeat: 'repeat-y',
            backgroundSize: `${PIECE_W}px ${EXT_H}px`,
            imageRendering: 'pixelated',
          }}
        />
      )}

      {/* The hand. Also the hit target — pressing anywhere on it grabs it. */}
      <div
        role="button"
        tabIndex={0}
        aria-label="Uzmi novčanicu"
        onPointerDown={(e) => {
          e.preventDefault();
          pressOrigin.current = { x: e.clientX, y: e.clientY };
          moved.current = false;
          setPos({ x: e.clientX, y: e.clientY });
          setPhase('held');
        }}
        onKeyDown={(e) => {
          // Keyboard path: the hand is not draggable, so offer the choice
          // directly rather than leaving it unreachable.
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            commit(alreadyBribed ?? 'armane');
          }
        }}
        className="no-touch-scroll fixed z-50 cursor-grab active:cursor-grabbing"
        style={{
          left: handLeft,
          top: handTop,
          width: PIECE_W,
          height: HAND_H,
          transform: risen ? 'translateY(0)' : `translateY(${REST_PEEK}px)`,
          transition: following || reduceMotion ? 'none' : 'transform 600ms cubic-bezier(.2,.8,.25,1), top 250ms ease-out, left 250ms ease-out',
        }}
      >
        <img
          src="/assets/arm_base.png"
          alt=""
          draggable={false}
          className="sprite pointer-events-none absolute left-0 top-0"
          style={{ width: PIECE_W, height: HAND_H }}
        />
        <img
          src="/assets/1000RSD.png"
          alt=""
          draggable={false}
          className="sprite pointer-events-none absolute"
          style={{ width: PIECE_W * 0.78, left: PIECE_W * 0.1, top: 14, transform: 'rotate(-6deg)' }}
        />
        <img
          src="/assets/arm_fingers.png"
          alt=""
          draggable={false}
          className="sprite pointer-events-none absolute left-0 top-0"
          style={{ width: PIECE_W, height: HAND_H }}
        />
      </div>

      {/* Whoever is under the note lights up, so the drop is never a guess. */}
      <style>{`[data-guard="${hovered ?? '__none__'}"]{filter:brightness(1.3) contrast(1.05) !important;opacity:1 !important}`}</style>

      {/* Declining is a first-class answer and must not read as an error — a
          plain visible control, no nagging, no penalty. If someone is
          borrowing a friend's phone, this has to actually work. */}
      <button
        type="button"
        onClick={() => commit(null)}
        className="fixed bottom-6 right-5 z-50 rounded-md border border-paper/20 bg-ink/80 px-3 py-2 text-xs uppercase tracking-widest text-paper/60"
      >
        Vrati u džep
      </button>

      {alreadyBribed && (
        <p className="pointer-events-none fixed inset-x-0 top-16 z-40 text-center text-xs uppercase tracking-[0.25em] text-paper/40">
          {alreadyBribed === 'armane' ? 'Armane već čeka' : 'Versace već čeka'}
        </p>
      )}
    </>
  );
}
